import $ from 'jquery'
import Backbone from 'backbone'
import KeyCodes from 'scoreboard/components/key-codes'
import _ from 'lodash'

/**
 * Listens to an input element for a variety of events to determine when it's
 * appropriate to apply the size change. For example, there's a 1 second delay
 * before applying the changes when typing, but using the up/down arrows is
 * applied immediately.
 *
 * See https://github.com/SpiderStrategies/Scoreboard/issues/30713#issuecomment-656237711
 * for full discussion.
 */
const WobSizeInputListener = Backbone.View.extend({
	events: {
		'keydown': 'onKeyDown',
		'paste': 'onPaste',
		'blur': 'onBlur',
		'input': 'apply'
	},

	initialize: function () {
		this.deferredTrigger = _.debounce(() => {
			delete this.inputPending
			this.trigger('apply')
		}, 1000)

		// #30713 listen for clicks outside the tooltip, which will cause the
		// tooltip to close, and immediately apply the size setting.
		this.onOutsideClick = (e) => {
			if ($(e.target).closest('.widget-config').length) {
				// If the click is inside the config tooltip, ignore it
				return
			}

			if (this.inputPending) {
				this.apply({ immediately: true })
			}
		}

		// Need to use capture: true option on event listener because canvas
		// already listens for mousedown and unselects all the wobs; listening
		// in the capture phase will let us apply the resize before canvas
		// does the unselect
		document.body.addEventListener(
			'mousedown', this.onOutsideClick, { capture: true })
	},

	onKeyDown: function (e) {
		if (e.keyCode == KeyCodes.UP || e.keyCode == KeyCodes.DOWN) {
			// UP or DOWN could increment/decrement the input, and we want to apply
			// that immediately. However, the input value won't be updated until
			// the 'input' event fires, so wait for that
			this.applyNextInput = true
		} else if (e.keyCode == KeyCodes.ENTER) {
			// 'input' event won't fire if ENTER was pressed, but it doesn't change
			// the input value, so we can apply the change immediately
			this.apply({ immediately: true })
		}
	},

	onPaste: function (e) {
		// We want to apply the pasted value immediately, but the input value won't
		// be updated until the 'input' event fires, so wait for that
		this.applyNextInput = true
	},

	onBlur: function () {
		// When we blur, we want to apply the change immediately
		this.apply({ immediately: true })
	},

	apply: function ({ immediately = false }) {
		let applyImmediately = immediately || this.applyNextInput
		delete this.applyNextInput

		if (applyImmediately) {
			this.deferredTrigger.cancel()
			this.trigger('apply')
		} else {
			this.inputPending = true
			this.deferredTrigger()
		}
	},

	remove: function () {
		Backbone.View.prototype.remove.apply(this, arguments)

		document.body.removeEventListener(
			'mousedown', this.onOutsideClick, { capture: true })
	}
})

export default WobSizeInputListener

WobSizeInputListener.__filename__ = 'dashboards/widgets/wob-size-input-listener.js'
